"use client";

import { useState, useEffect, useCallback } from "react";
import { Shield, Plus, Trash2, Check, X, ChevronDown, ChevronRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  fetchCustomRoles,
  createCustomRole,
  updateCustomRole,
  deleteCustomRole,
  fetchEmployeesWithRoles,
  assignRoleToEmployee,
  removeRoleFromEmployee,
  getUserPermissions,
} from "@/lib/supabase/rbac-actions";
import { PERMISSIONS_LIST, PERMISSION_CATEGORIES, type Permission } from "@/lib/permissions";
import PageSkeleton from "./PageSkeleton";

const ROLE_COLORS = [
  "#1D9E75", "#6366F1", "#F59E0B", "#EF4444", "#EC4899",
  "#8B5CF6", "#06B6D4", "#10B981", "#F97316", "#6B7280",
];

export default function RolesPage() {
  const [loading, setLoading] = useState(true);
  const [canManage, setCanManage] = useState(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);

  // Role editing
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [roleName, setRoleName] = useState("");
  const [roleColor, setRoleColor] = useState(ROLE_COLORS[0]);
  const [roleHierarchy, setRoleHierarchy] = useState(3);
  const [rolePerms, setRolePerms] = useState<Set<Permission>>(new Set());
  const [canManageLower, setCanManageLower] = useState(false);
  const [isPayable, setIsPayable] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [showDeleteRole, setShowDeleteRole] = useState(false);

  // Employee assignment
  const [assigningRoleId, setAssigningRoleId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getUserPermissions().catch(() => ({ orgRole: "employee" })),
      fetchCustomRoles().catch(() => []),
      fetchEmployeesWithRoles().catch(() => []),
    ])
      .then(([perms, r, e]) => {
        if (!cancelled) {
          setCanManage(perms.orgRole === "owner" || perms.orgRole === "admin");
          setRoles(r);
          setEmployees(e);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const openRoleEditor = useCallback((role: any | null) => {
    setEditingRole(role);
    setRoleName(role?.name || "");
    setRoleColor(role?.color || ROLE_COLORS[0]);
    setRoleHierarchy(role?.hierarchy_level || 3);
    setCanManageLower(role?.can_manage_lower || false);
    setIsPayable(role?.is_payable || false);
    const perms = new Set<Permission>((role?.role_permissions || []).map((p: any) => p.permission) as Permission[]);
    setRolePerms(perms);
    setShowDeleteRole(false);
  }, []);

  const handleSaveRole = async () => {
    if (!roleName.trim()) return;
    setSavingRole(true);
    try {
      if (editingRole) {
        await updateCustomRole(editingRole.id, roleName, roleHierarchy, Array.from(rolePerms), roleColor, canManageLower);
      } else {
        await createCustomRole(roleName, roleHierarchy, Array.from(rolePerms), roleColor, canManageLower);
      }
      const updated = await fetchCustomRoles();
      setRoles(updated);
      setEditingRole(null);
    } catch (e: any) {
      alert(e.message || "Failed to save role");
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!editingRole) return;
    try {
      await deleteCustomRole(editingRole.id);
      const updated = await fetchCustomRoles();
      setRoles(updated);
      setEditingRole(null);
    } catch (e: any) {
      alert(e.message || "Failed to delete role");
    }
    setShowDeleteRole(false);
  };

  const handleAssignRole = async (employeeId: string, roleId: string) => {
    await assignRoleToEmployee(employeeId, roleId);
    const emps = await fetchEmployeesWithRoles();
    setEmployees(emps);
  };

  const handleRemoveRole = async (employeeId: string, roleId: string) => {
    await removeRoleFromEmployee(employeeId, roleId);
    const emps = await fetchEmployeesWithRoles();
    setEmployees(emps);
  };

  if (loading) return <PageSkeleton name="Roles" />;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold text-gray-900">Roles & Members</h1>
          <p className="text-gray-500 mt-1">Manage roles, permissions, and member assignments</p>
        </div>
        {canManage && (
          <Button onClick={() => openRoleEditor(null)} className="bg-[#1D9E75] hover:bg-[#1a8b66] text-white gap-2 cursor-pointer">
            <Plus className="w-4 h-4" />
            Create Role
          </Button>
        )}
      </div>

      {/* Role Editor Modal */}
      {editingRole && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditingRole(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">{editingRole ? "Edit Role" : "Create Role"}</h3>
              <button onClick={() => setEditingRole(null)} className="p-1 hover:bg-gray-100 rounded cursor-pointer">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {showDeleteRole ? (
              <div className="p-4 bg-red-50 rounded-lg space-y-3">
                <p className="text-sm text-red-700 font-medium">Delete this role permanently?</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowDeleteRole(false)} className="cursor-pointer text-xs">Cancel</Button>
                  <Button size="sm" onClick={handleDeleteRole} className="bg-red-500 hover:bg-red-600 text-white text-xs cursor-pointer">Delete</Button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase">Role Name</label>
                  <input value={roleName} onChange={(e) => setRoleName(e.target.value)} placeholder="e.g. Finance Lead"
                    className="mt-1.5 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300" />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase">Color</label>
                  <div className="flex gap-2 mt-1.5 flex-wrap">
                    {ROLE_COLORS.map((c) => (
                      <button key={c} onClick={() => setRoleColor(c)}
                        className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 cursor-pointer ${roleColor === c ? "border-gray-900 scale-110" : "border-transparent"}`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase">Hierarchy Level: {roleHierarchy}</label>
                  <input type="range" min="0" max="100" value={roleHierarchy} onChange={(e) => setRoleHierarchy(parseInt(e.target.value))}
                    className="w-full mt-1.5 accent-teal-600" />
                  <p className="text-xs text-gray-400 mt-1">Higher roles manage lower roles.</p>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <input type="checkbox" checked={canManageLower} onChange={(e) => setCanManageLower(e.target.checked)}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Can manage roles below this level</p>
                    <p className="text-xs text-gray-500">Users can assign/remove roles with lower hierarchy levels.</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <input type="checkbox" checked={isPayable} onChange={(e) => setIsPayable(e.target.checked)}
                    className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Is payable</p>
                    <p className="text-xs text-gray-500">Members with this role appear in payroll pay logs.</p>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-600 uppercase">Permissions</label>
                  <div className="mt-2 space-y-3 max-h-48 overflow-y-auto">
                    {PERMISSION_CATEGORIES.map((cat) => (
                      <div key={cat}>
                        <p className="text-[10px] font-medium text-gray-500 mb-1">{cat}</p>
                        <div className="grid grid-cols-2 gap-1">
                          {PERMISSIONS_LIST.filter((p) => p.category === cat).map((perm) => (
                            <label key={perm.key} className="flex items-center gap-1.5 py-0.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={rolePerms.has(perm.key)}
                                onChange={(e) => {
                                  const next = new Set(rolePerms);
                                  if (e.target.checked) next.add(perm.key as Permission);
                                  else next.delete(perm.key as Permission);
                                  setRolePerms(next);
                                }}
                                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                              />
                              <span className="text-xs text-gray-700">{perm.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  {editingRole && (
                    <Button variant="outline" size="sm" onClick={() => setShowDeleteRole(true)} className="gap-1 cursor-pointer text-xs text-red-600 border-red-200">
                      <Trash2 className="w-3 h-3" /> Delete
                    </Button>
                  )}
                  <div className="flex-1" />
                  <Button onClick={handleSaveRole} disabled={savingRole || !roleName.trim()} className="bg-teal-600 hover:bg-teal-700 text-white gap-1 cursor-pointer">
                    {savingRole ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                    {savingRole ? "Saving..." : (editingRole ? "Save" : "Create")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Role List with Member Assignments */}
      <div className="space-y-3">
        {/* System Roles */}
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">System Roles</h2>
        {[
          { name: "Owner", color: "#EF4444", level: 10, perms: "All permissions" },
          { name: "Admin", color: "#F59E0B", level: 9, perms: "All permissions" },
          { name: "Employee", color: "#6B7280", level: 1, perms: "Basic access" },
        ].map((r) => (
          <div key={r.name} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: r.color }} />
              <span className="text-sm font-semibold text-gray-900">{r.name}</span>
              <span className="text-xs text-gray-400">Level {r.level}</span>
              <span className="text-xs text-gray-400 ml-auto">{r.perms}</span>
            </div>
          </div>
        ))}

        {/* Custom Roles */}
        {roles.length > 0 && (
          <>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mt-6">Custom Roles</h2>
            {roles
              .sort((a, b) => (b.hierarchy_level || 0) - (a.hierarchy_level || 0))
              .map((role) => {
                const isExpanded = expandedRole === role.id;
                const members = employees.filter((e) =>
                  (e.employee_roles || []).some((er: any) => er.role_id === role.id)
                );

                return (
                  <div key={role.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => setExpandedRole(isExpanded ? null : role.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    >
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: role.color || ROLE_COLORS[0] }} />
                      <span className="text-sm font-semibold text-gray-900">{role.name}</span>
                      <span className="text-xs text-gray-400">Level {role.hierarchy_level}</span>
                      <Badge variant="outline" className="text-[10px] border-gray-200">
                        {members.length} member{members.length !== 1 ? "s" : ""}
                      </Badge>
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" /> : <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />}
                    </button>

                    {isExpanded && (
                      <div className="border-t border-gray-100 px-4 py-3 space-y-3">
                        {/* Members */}
                        {members.length === 0 ? (
                          <p className="text-xs text-gray-400">No members assigned.</p>
                        ) : (
                          <div className="space-y-1">
                            {members.map((emp: any) => (
                              <div key={emp.id} className="flex items-center justify-between py-1.5">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-white text-[8px] font-semibold">
                                    {emp.first_name[0]}{emp.last_name[0]}
                                  </div>
                                  <span className="text-sm text-gray-900">{emp.first_name} {emp.last_name}</span>
                                </div>
                                {canManage && (
                                  <button onClick={() => handleRemoveRole(emp.id, role.id)} className="p-1 text-gray-400 hover:text-red-500 cursor-pointer">
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Assign */}
                        {canManage && (
                          <div>
                            {assigningRoleId === role.id ? (
                              <div className="space-y-1 p-2 bg-gray-50 rounded">
                                {employees
                                  .filter((e) => !(e.employee_roles || []).some((er: any) => er.role_id === role.id))
                                  .map((emp: any) => (
                                    <button
                                      key={emp.id}
                                      onClick={() => { handleAssignRole(emp.id, role.id); setAssigningRoleId(null); }}
                                      className="w-full text-left px-2 py-1 hover:bg-gray-100 rounded cursor-pointer"
                                    >
                                      <span className="text-xs text-gray-900">{emp.first_name} {emp.last_name}</span>
                                    </button>
                                  ))}
                                <button onClick={() => setAssigningRoleId(null)} className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer">Cancel</button>
                              </div>
                            ) : (
                              <Button variant="outline" size="sm" onClick={() => setAssigningRoleId(role.id)} className="gap-1 cursor-pointer text-xs">
                                <Users className="w-3 h-3" /> Add Member
                              </Button>
                            )}
                          </div>
                        )}

                        {/* Edit */}
                        {canManage && (
                          <Button variant="outline" size="sm" onClick={() => openRoleEditor(role)} className="gap-1 cursor-pointer text-xs">
                            Edit Role
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </>
        )}
      </div>
    </div>
  );
}
